import type {
  HarnessEvent,
  HarnessItemResult,
  HarnessItemStatus,
  HarnessRun,
} from '@borradh-workspace/api-client/types';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Square,
  TerminalSquare,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { useCancelHarnessRun } from '../../api';

export const STATUS_META: Record<
  HarnessItemStatus,
  { label: string; icon: typeof CheckCircle2; tone: string; chip: string }
> = {
  pass: {
    label: 'Passed',
    icon: CheckCircle2,
    tone: 'text-emerald-600 dark:text-emerald-400',
    chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  },
  fail: {
    label: 'Failed',
    icon: XCircle,
    tone: 'text-rose-600 dark:text-rose-400',
    chip: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20',
  },
  reply: {
    label: 'Replied',
    icon: MessageSquareText,
    tone: 'text-sky-600 dark:text-sky-400',
    chip: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20',
  },
  no_reply: {
    label: 'No reply',
    icon: AlertTriangle,
    tone: 'text-amber-600 dark:text-amber-400',
    chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
  },
  error: {
    label: 'Error',
    icon: XCircle,
    tone: 'text-rose-600 dark:text-rose-400',
    chip: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20',
  },
};

const GATE_LABELS: Record<string, string> = {
  ingress: 'Inbound',
  source: 'Clinic data',
  generation: 'Model',
  turn: 'Turn',
  tool_effects: 'Tools',
  review: 'Review',
  flow: 'Worker',
  persistence: 'Saved',
  delivery: 'Sent',
};

export function ModeBadge({ mode }: { mode: HarnessRun['mode'] }) {
  return mode === 'live' ? (
    <Badge
      variant="outline"
      className="gap-1 border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300"
    >
      <Sparkles className="size-3" />
      Live AI
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1">
      <ShieldCheck className="size-3" />
      Recorded
    </Badge>
  );
}

export function StatusChip({ status }: { status: HarnessItemStatus }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium text-xs',
        meta.chip
      )}
    >
      <Icon className="size-3.5" />
      {meta.label}
    </span>
  );
}

function ClaireBubble({
  text,
  tone = 'default',
  label,
}: {
  text: string;
  tone?: 'default' | 'bad';
  label?: string;
}) {
  return (
    <div className="space-y-1">
      {label ? (
        <p className="font-medium text-muted-foreground text-xs">{label}</p>
      ) : null}
      <div
        className={cn(
          'whitespace-pre-wrap rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed',
          tone === 'bad'
            ? 'border border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40'
            : 'bg-muted'
        )}
      >
        {text}
      </div>
    </div>
  );
}

const CLAIM_LABELS: Record<string, string> = {
  money: 'price',
  price: 'price',
  discount: 'discount',
  deposit: 'deposit',
  location: 'location',
  clinical: 'clinical claim',
  availability: 'availability promise',
  booking: 'booking promise',
  handoff: 'handoff promise',
  media: 'media promise',
  ad: 'ad wording',
};

/** `unsupported_handoff_draft_sanitized` → `handoff promise`. */
function claimLabel(type: unknown): string {
  const raw = String(type ?? '');
  if (CLAIM_LABELS[raw]) return CLAIM_LABELS[raw];
  const core = raw
    .split('_')
    .filter(
      (word) =>
        word && !['unsupported', 'draft', 'sanitized', 'claim'].includes(word)
    );
  const known = core.find((word) => CLAIM_LABELS[word]);
  if (known) return CLAIM_LABELS[known] ?? known;
  return core.join(' ') || 'claim';
}

const GATE_REASONS: Record<string, string> = {
  branch_unknown: 'the branch isn’t known yet',
  no_calendar: 'the clinic has no connected calendar',
  books_externally: 'the clinic books on its own system',
};

export function describeEvent(event: HarnessEvent): string | null {
  const meta = (event.metadata ?? {}) as Record<string, unknown>;
  const quoted = (value: unknown) => (value ? `: “${String(value)}”` : '');
  switch (event.event) {
    case 'grounding_violation':
      return `Blocked an unsupported ${claimLabel(meta.type)}${quoted(meta.claim)}`;
    case 'rule_violation':
      return `${meta.severity === 'block' ? 'Blocked' : 'Warned'}: broke the “${String(meta.rule ?? 'reply')}” rule${meta.detail ? ` (${String(meta.detail)})` : ''}`;
    case 'fabrication_detected':
      return `Reviewer caught a possible made-up claim${quoted(meta.quote)}${meta.why ? `. ${String(meta.why)}` : ''}`;
    case 'fabrication_overruled':
      return `Reviewer flagged${quoted(meta.quote)}, but the ad supports it, so it stood`;
    case 'suspicious_claim':
      return `Flagged for a person to check, not blocked${quoted(meta.quote)}`;
    case 'review_retry':
    case 'turn_retry':
      return 'Sent the draft back for a rewrite';
    case 'review_exhausted':
      return 'Gave up rewriting and used the safe fallback';
    case 'rounds_exhausted':
      return 'Ran out of tool rounds before finishing';
    case 'handoff':
    case 'handoff_requested':
      return 'Handed the conversation to a human';
    case 'grounding_passed':
      return 'Every price and claim was backed by clinic data';
    default:
      return null;
  }
}

/** One line per reason, instead of one per held-back tool. */
export function describeGatedTools(events: HarnessEvent[]): string[] {
  const byReason = new Map<string, string[]>();
  for (const event of events) {
    if (event.event !== 'capability_gated') continue;
    const meta = (event.metadata ?? {}) as Record<string, unknown>;
    const reason = String(meta.reason ?? 'unknown');
    const tools = byReason.get(reason) ?? [];
    tools.push(String(meta.tool ?? 'tool'));
    byReason.set(reason, tools);
  }
  return [...byReason].map(
    ([reason, tools]) =>
      `Held back ${tools.join(', ')} because ${GATE_REASONS[reason] ?? reason.replaceAll('_', ' ')}`
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Wrench;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs uppercase tracking-wide">
        <Icon className="size-3.5" />
        {title}
      </p>
      {children}
    </div>
  );
}

export function HarnessItemCard({
  item,
  defaultOpen = false,
}: {
  item: HarnessItemResult;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const drafts = (item.drafts ?? []).filter((draft) => draft.trim());
  const blocked = drafts.filter((draft) => draft !== item.reply);
  const events = [
    ...(item.events ?? [])
      .map((event) => ({ event, text: describeEvent(event) }))
      .filter((entry): entry is { event: HarnessEvent; text: string } =>
        Boolean(entry.text)
      ),
    ...describeGatedTools(item.events ?? []).map((text) => ({
      event: { event: 'capability_gated', metadata: null },
      text,
    })),
  ];
  const failedChecks = (item.checks ?? []).filter((check) => !check.pass);
  const gates = Object.entries(item.gates ?? {});
  const tools = item.tools ?? [];
  const hasDetails =
    blocked.length > 0 ||
    (item.checks?.length ?? 0) > 0 ||
    gates.length > 0 ||
    tools.length > 0 ||
    events.length > 0;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-row items-start justify-between gap-3 border-b bg-muted/30 px-5 py-3">
        <div className="min-w-0 space-y-0.5">
          <p className="truncate font-medium text-sm">{item.title}</p>
          {item.note ? (
            <p className="text-muted-foreground text-xs">{item.note}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {item.durationMs > 0 ? (
            <span className="text-muted-foreground text-xs tabular-nums">
              {(item.durationMs / 1000).toFixed(1)}s
            </span>
          ) : null}
          <StatusChip status={item.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-5 py-4">
        {item.error ? (
          <p className="rounded-md bg-rose-500/5 px-3 py-2 font-mono text-rose-700 text-xs dark:text-rose-300">
            {item.error}
          </p>
        ) : null}

        {item.originalReply !== undefined && item.originalReply !== null ? (
          <div className="grid gap-4 md:grid-cols-2">
            <ClaireBubble
              label="What Claire sent then"
              text={item.originalReply || '(nothing)'}
              tone="bad"
            />
            <ClaireBubble
              label="What Claire does now"
              text={item.reply?.trim() || '(no reply)'}
            />
          </div>
        ) : item.reply?.trim() ? (
          <ClaireBubble text={item.reply} />
        ) : null}

        {failedChecks.length > 0 ? (
          <ul className="space-y-1">
            {failedChecks.map((check) => (
              <li
                key={check.label}
                className="flex items-center gap-2 text-rose-700 text-sm dark:text-rose-300"
              >
                <XCircle className="size-4 shrink-0" />
                {check.label}
              </li>
            ))}
          </ul>
        ) : null}

        {hasDetails ? (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="-ml-2 h-7 gap-1">
                <ChevronDown
                  className={cn(
                    'size-4 transition-transform',
                    open && 'rotate-180'
                  )}
                />
                {open ? 'Hide' : 'Show'} what happened
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-5">
              {gates.length > 0 ? (
                <Section title="Pipeline" icon={CircleDashed}>
                  <div className="flex flex-wrap gap-1.5">
                    {gates.map(([gate, ran]) => (
                      <span
                        key={gate}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs',
                          ran
                            ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                            : 'border-dashed text-muted-foreground'
                        )}
                        title={
                          ran
                            ? 'Ran through production code'
                            : 'Substituted by a recording'
                        }
                      >
                        {ran ? (
                          <CheckCircle2 className="size-3" />
                        ) : (
                          <CircleDashed className="size-3" />
                        )}
                        {GATE_LABELS[gate] ?? gate}
                      </span>
                    ))}
                  </div>
                </Section>
              ) : null}

              {(item.checks?.length ?? 0) > 0 ? (
                <Section title="Checks" icon={ShieldCheck}>
                  <ul className="space-y-1">
                    {item.checks?.map((check) => (
                      <li
                        key={check.label}
                        className="flex items-start gap-2 text-sm"
                      >
                        {check.pass ? (
                          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <XCircle className="mt-0.5 size-4 shrink-0 text-rose-600 dark:text-rose-400" />
                        )}
                        {check.label}
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {blocked.length > 0 ? (
                <Section title="Drafts that never went out" icon={Ban}>
                  <ul className="space-y-2">
                    {blocked.map((draft, index) => (
                      <li
                        key={index}
                        className="rounded-lg border border-dashed px-3 py-2 text-muted-foreground text-sm line-through decoration-rose-500/60"
                      >
                        {draft}
                      </li>
                    ))}
                  </ul>
                  {item.review ? (
                    <p className="text-muted-foreground text-xs">
                      Review: {item.review.status.replace('_', ' ')}
                      {item.review.retryCount
                        ? ` after ${item.review.retryCount} rewrite${item.review.retryCount === 1 ? '' : 's'}`
                        : ''}
                    </p>
                  ) : null}
                </Section>
              ) : null}

              {events.length > 0 ? (
                <Section title="Guards" icon={ShieldCheck}>
                  <ul className="space-y-1">
                    {events.map(({ event, text }, index) => (
                      <li
                        key={`${event.event}-${index}`}
                        className={cn(
                          'text-sm',
                          [
                            'grounding_violation',
                            'rule_violation',
                            'fabrication_detected',
                            'suspicious_claim',
                          ].includes(event.event)
                            ? 'text-amber-700 dark:text-amber-300'
                            : 'text-muted-foreground'
                        )}
                      >
                        {text}
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {tools.length > 0 ? (
                <Section title="Tools Claire used" icon={Wrench}>
                  <div className="flex flex-wrap gap-1.5">
                    {tools.map((tool, index) => (
                      <Badge
                        key={`${tool.name}-${index}`}
                        variant="outline"
                        className={cn(
                          'font-mono',
                          tool.blocked && 'border-dashed text-muted-foreground'
                        )}
                        title={
                          tool.blocked
                            ? 'Blocked: this would act outside the sandbox'
                            : tool.simulated
                              ? 'Recorded result'
                              : 'Ran against the copied clinic data'
                        }
                      >
                        {tool.name}
                        {tool.blocked ? ' · blocked' : ''}
                      </Badge>
                    ))}
                  </div>
                </Section>
              ) : null}

              {item.delivery?.length ? (
                <Section title="Would have sent" icon={MessageSquareText}>
                  <ul className="space-y-1 text-sm">
                    {item.delivery.map((part, index) => (
                      <li key={index} className="flex items-center gap-2">
                        {part.accepted ? (
                          <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <XCircle className="size-4 text-rose-600 dark:text-rose-400" />
                        )}
                        <span className="text-muted-foreground">
                          {part.channel.replace('_', ' ')}
                        </span>
                        <span className="truncate">{part.text}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {item.modelCalls ? (
                <p className="text-muted-foreground text-xs">
                  {item.modelCalls} model call{item.modelCalls === 1 ? '' : 's'}
                  {item.totalTokens
                    ? ` · ${item.totalTokens.toLocaleString()} tokens`
                    : ''}
                </p>
              ) : null}
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </CardContent>
    </Card>
  );
}

function useElapsed(startedAt: string, finishedAt: string | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (finishedAt) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [finishedAt]);
  const end = finishedAt ? Date.parse(finishedAt) : now;
  return Math.max(0, Math.round((end - Date.parse(startedAt)) / 1000));
}

export function HarnessRunView({ run }: { run: HarnessRun }) {
  const { cancelRun, isCancelling } = useCancelHarnessRun();
  const elapsed = useElapsed(run.startedAt, run.finishedAt);
  const running = run.status === 'running';
  const passed = run.items.filter((item) => item.status === 'pass').length;
  const failed = run.items.filter(
    (item) => item.status === 'fail' || item.status === 'error'
  ).length;
  const [showLog, setShowLog] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <ModeBadge mode={run.mode} />
            <span className="text-muted-foreground text-xs tabular-nums">
              {elapsed}s
            </span>
          </div>
          <p className="truncate font-semibold">{run.label}</p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => cancelRun(run.id)}
              disabled={isCancelling}
            >
              <Square className="size-3.5" />
              Stop
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLog((value) => !value)}
          >
            <TerminalSquare className="size-4" />
            {showLog ? 'Hide' : 'Runner'} output
          </Button>
        </div>
      </div>

      {running ? (
        <div className="space-y-2 rounded-xl border bg-muted/30 p-4">
          <div className="flex items-center gap-2 text-sm">
            <Spinner className="size-4" />
            {run.items.length === 0
              ? elapsed < 8
                ? 'Starting a throwaway database…'
                : run.mode === 'live'
                  ? 'Claire is thinking…'
                  : 'Replaying through the production pipeline…'
              : `${run.items.length} of ${run.total} done`}
          </div>
          <Progress
            value={
              run.total
                ? (100 * Math.max(run.items.length, 0.15)) / run.total
                : 0
            }
          />
        </div>
      ) : run.status === 'failed' || run.status === 'cancelled' ? (
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4 text-sm">
          {run.status === 'cancelled'
            ? 'Stopped.'
            : (run.error ?? 'The run failed.')}{' '}
          {run.status === 'failed' ? 'Open the runner output for details.' : ''}
        </div>
      ) : run.total > 1 ? (
        <div className="flex gap-4 text-sm">
          {passed ? (
            <span className="text-emerald-700 dark:text-emerald-300">
              {passed} passed
            </span>
          ) : null}
          {failed ? (
            <span className="text-rose-700 dark:text-rose-300">
              {failed} failed
            </span>
          ) : null}
          <span className="text-muted-foreground">{run.total} total</span>
        </div>
      ) : null}

      {showLog ? (
        <pre className="max-h-72 overflow-auto rounded-xl bg-zinc-950 p-4 font-mono text-[11px] text-zinc-300 leading-relaxed">
          {run.logTail.length ? run.logTail.join('\n') : 'No output yet.'}
        </pre>
      ) : null}

      <div className="space-y-3">
        {run.items.map((item) => (
          <HarnessItemCard
            key={item.id}
            item={item}
            defaultOpen={run.items.length === 1}
          />
        ))}
      </div>
    </div>
  );
}
